import type { MouseEvent } from "react";

import dayjs from "dayjs";

import type { BacklinksReferringDomainsReferringDomain } from "@similarweb/pro-api-client";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { shortMonthDayAndShortYear } from "@similarweb/pro-logic-components";

import type { Translate } from "app/filters/i18nFilter";
import { WebsiteTooltipTopCell } from "app/components/React/Table/cells";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { BacklinkExpectedURLParams } from "../../../shared/BacklinksTypes";
import { IndexCell, numFilter } from "../../../shared/BacklinksUtils";
import { DynamicRulesToQueryString } from "../../backlinks/components/filters/include-exclude-filter/include-exclude.utils";

import { AntWebsiteTooltipTopCellWrapper } from "./BacklinksDomainsStyledComponents";

interface IGetBacklinksDomainsColumnsProps {
    handleOpenQualificationSideBar?: <T extends { Domain: string; Favicon: string }>(row: T) => void;
    translate: Translate;
    params: { sort?: string; asc?: string };
    rowsCount?: number;
    currentPage?: number;
}

function handleCountClick(e: MouseEvent<HTMLAnchorElement>, rowData: BacklinksReferringDomainsReferringDomain) {
    e.preventDefault();
    e.stopPropagation();
    const params = proNavigator.getParams() as BacklinkExpectedURLParams;
    void proNavigator.go(
        "digitalsuite_backlinks_table",
        {
            key: params.key,
            duration: params.duration,
            country: params.country,
            includeExclude: DynamicRulesToQueryString([
                {
                    type: "include",
                    fieldName: "UrlFrom",
                    matchType: "partial",
                    items: [{ text: rowData.Name }],
                },
                {
                    type: "include",
                    fieldName: "UrlTo",
                    matchType: "partial",
                    items: [{ text: params.key as string }],
                },
            ]),
        },
        {
            location: true,
        },
    );
}

export const getBacklinksDomainsColumns = ({
    handleOpenQualificationSideBar,
    translate,
    params,
    rowsCount = 0,
    currentPage = 1,
}: IGetBacklinksDomainsColumnsProps): ColumnsType<BacklinksReferringDomainsReferringDomain> => {
    return [
        {
            width: 50,
            render: IndexCell(currentPage),
            // Index never changes as the data comes in single page batches
            // And because we cache the results the loading state does not clears out previous rendered cells
            // Therefore we need to make sure this cell is always updated
            shouldCellUpdate: () => true,
            align: "center",
            resize: false,
        },
        {
            dataIndex: "Name",
            title: `${translate("digitalsuite.backlinks.refdomains.table.column.refdomain")} (${numFilter(rowsCount)})`,
            tooltipSubtitle: translate("digitalsuite.backlinks.refdomains.table.column.refdomain.tooltip"),
            render: (val, row) => {
                return (
                    <AntWebsiteTooltipTopCellWrapper>
                        <WebsiteTooltipTopCell
                            value={val}
                            row={row}
                            handleOpenQualificationSideBar={handleOpenQualificationSideBar}
                        />
                    </AntWebsiteTooltipTopCellWrapper>
                );
            },
        },
        {
            // The original type does not support `width` as string because of calculations in the table columnFormatter function
            // String is supported by the table natively, therefore we disable resize and set the value as % in the table
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width: "18%",
            dataIndex: "Rank",
            align: "right",
            title: translate("digitalsuite.backlinks.table.column.domain_inlink_rank"),
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.domain_inlink_rank.tooltip"),
            resize: false,
            sorter: true,
            sortOrder: params.sort === "Rank" ? (params.asc === "true" ? "ascend" : "descend") : undefined,
        },
        {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width: "12%",
            align: "right",
            title: translate("digitalsuite.backlinks.refdomains.column.backlinks"),
            tooltipSubtitle: translate("digitalsuite.backlinks.refdomains.column.backlinks.tooltip"),
            dataIndex: "BacklinksCount",
            render: (value, row) => {
                return (
                    <a
                        // href={url}
                        aria-label={`link to Backlinks for domain ${row.Name}`}
                        onClick={(e) => handleCountClick(e, row)}
                    >
                        {value.toLocaleString()}
                    </a>
                );
            },
            resize: false,
            sorter: true,
            sortOrder: params.sort === "BacklinksCount" ? (params.asc === "true" ? "ascend" : "descend") : undefined,
        },
        {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width: "12%",
            align: "right",
            title: translate("digitalsuite.backlinks.refdomains.column.follow.tooltip.dofollow"),
            tooltipSubtitle: translate("digitalsuite.backlinks.refdomains.column.follow.tooltip"),
            dataIndex: "ReferringPagesFollow",
            render: (value) => value.toLocaleString(),
            resize: false,
        },
        {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width: "12%",
            align: "right",
            title: translate("digitalsuite.backlinks.refdomains.column.follow.tooltip.nofollow"),
            tooltipSubtitle: translate("digitalsuite.backlinks.refdomains.column.follow.tooltip"),
            dataIndex: "ReferringPagesNofollow",
            render: (value) => value.toLocaleString(),
            resize: false,
        },
        {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            width: "12%",
            align: "right",
            render: (value) => dayjs(value as string).format(shortMonthDayAndShortYear),
            title: translate("digitalsuite.backlinks.table.column.first_seen"),
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.first_seen.tooltip"),
            dataIndex: "FirstSeen",
            sorter: true,
            sortOrder: params.sort === "FirstSeen" ? (params.asc === "true" ? "ascend" : "descend") : undefined,
            fixed: "right",
            resize: false,
        },
    ];
};
